import { supabase } from '../lib/supabase';

export type ProjectFileEntry = { type: string; name: string; url: string };

export async function insertProjectDocumentRow(input: {
  projectId: string;
  designId?: string | null;
  docType: string;
  storageBucket?: string;
  storagePath: string;
  publicUrl: string;
  fileName: string;
  mimeType?: string | null;
  bytes?: number | null;
}): Promise<{ error: Error | null }> {
  const { error } = await supabase.from('project_documents').insert({
    project_id: input.projectId,
    design_id: input.designId ?? null,
    doc_type: input.docType,
    storage_bucket: input.storageBucket ?? 'project_files',
    storage_path: input.storagePath,
    public_url: input.publicUrl,
    file_name: input.fileName,
    mime_type: input.mimeType ?? null,
    bytes: input.bytes ?? null,
  });
  if (error) return { error: new Error(error.message) };
  return { error: null };
}

export async function appendProjectFilesEntry(projectId: string, entry: ProjectFileEntry): Promise<void> {
  const { data, error } = await supabase.from('projects').select('files').eq('id', projectId).single();
  if (error) {
    console.error('appendProjectFilesEntry fetch:', error);
    return;
  }
  const next = [...((data?.files as ProjectFileEntry[] | null) || []), entry];
  const { error: uerr } = await supabase
    .from('projects')
    .update({ files: next, updated_at: new Date().toISOString() })
    .eq('id', projectId);
  if (uerr) console.error('appendProjectFilesEntry update:', uerr);
}

export async function insertDocumentWithOptionalFilesDualWrite(opts: {
  projectId: string;
  designId?: string | null;
  docType: string;
  storagePath: string;
  publicUrl: string;
  fileName: string;
  mimeType?: string | null;
  bytes?: number | null;
  filesAppend?: ProjectFileEntry | null;
}): Promise<void> {
  const { error } = await insertProjectDocumentRow({
    projectId: opts.projectId,
    designId: opts.designId,
    docType: opts.docType,
    storagePath: opts.storagePath,
    publicUrl: opts.publicUrl,
    fileName: opts.fileName,
    mimeType: opts.mimeType,
    bytes: opts.bytes,
  });
  if (error) console.error('insertProjectDocumentRow:', error);
  if (opts.filesAppend) await appendProjectFilesEntry(opts.projectId, opts.filesAppend);
}

export async function uploadPdfAndRecord(opts: {
  projectId: string;
  designId?: string | null;
  docType: string;
  blob: Blob;
  storageFileBase: string;
  filesAppendType?: string;
}): Promise<void> {
  const safeBase = opts.storageFileBase.replace(/[^a-z0-9_-]/gi, '_').slice(0, 80);
  const path = `solar_reports/${opts.projectId}/${Date.now()}-${safeBase}.pdf`;
  const { error: upErr } = await supabase.storage.from('project_files').upload(path, opts.blob, {
    contentType: 'application/pdf',
    upsert: false,
  });
  if (upErr) {
    console.error('uploadPdfAndRecord storage:', upErr);
    return;
  }
  const { data: urlData } = supabase.storage.from('project_files').getPublicUrl(path);
  const pub = urlData.publicUrl;
  const fileName = `${safeBase}.pdf`;
  await insertDocumentWithOptionalFilesDualWrite({
    projectId: opts.projectId,
    designId: opts.designId,
    docType: opts.docType,
    storagePath: path,
    publicUrl: pub,
    fileName,
    mimeType: 'application/pdf',
    bytes: opts.blob.size,
    filesAppend:
      opts.filesAppendType != null
        ? { type: opts.filesAppendType, name: fileName, url: pub }
        : null,
  });
}
